import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { BiHomeAlt } from "react-icons/bi";
import { GoPlus } from "react-icons/go";
import { FiLoader } from "react-icons/fi";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ContentControllerForm from "components/common/ContentControlForm";
import AboutPageActionsDropdown from "components/dropdown/AboutDropdown";
import AboutPageViewModal from "components/modal/AboutPageViewModal";
import "styles/admin.css";

const API_BASE = "https://algoritm-api-edu.uz";

const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const ContentController = ({ goBack }) => {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const handleView = async (id) => {
    try {
      setViewLoading(true);
      setViewModalOpen(true);

      const res = await api.get(`/api/admin/AboutPage/GetById/${id}`);
      const item = res.data?.data || res.data || null;

      if (!item?.id) {
        toast.error("Ma'lumot topilmadi");
        setViewModalOpen(false);
        return;
      }

      setSelectedContent({
        ...item,
        imageUrl: item.imageLink
          ? item.imageLink.startsWith("http")
            ? item.imageLink
            : `${API_BASE}/${item.imageLink}`
          : null,
        displayStatus: item.status || "Pending",
      });
    } catch (err) {
      console.error(err);
      toast.error("Ma'lumotlarni yuklab bo'lmadi");
      setViewModalOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const fetchContents = async () => {
    setLoading(true);
    try {
      const res = await api.post("/api/admin/AboutPage/GetAll", {
        page: 1,
        pageSize: 100,
      });

      const rows = res.data?.rows || res.data?.data || [];
      const enhanced = rows.map((item) => ({
        ...item,
        displayStatus: item.status || "Pending",
      }));

      setContents(enhanced.sort((a, b) => b.id - a.id));
    } catch (err) {
      toast.error("Ma'lumotlar yuklanmadi");
      setContents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContents();
  }, []);

  const handleAdd = async (formData) => {
    if (!formData.image) {
      return toast.error("Rasm yuklash majburiy!");
    }

    const data = new FormData();
    data.append("Title", formData.title.trim());
    data.append(
      "ShortDescription",
      formData.shortDescription?.trim() || formData.title.trim()
    );
    data.append("FullDescription", formData.fullDescription?.trim() || "");
    data.append("Img", formData.image);

    try {
      await api.post("/api/admin/AboutPage/Add", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Muvaffaqiyatli qo‘shildi!");
      setShowForm(false);
      fetchContents();
    } catch (err) {
      const errorMsg =
        err.response?.data?.errorResult?.errorMessage ||
        err.response?.data?.message ||
        "Qo‘shishda xato yuz berdi";
      toast.error(errorMsg);
    }
  };

  const handleEdit = async (id) => {
    try {
      const res = await api.get(`/api/admin/AboutPage/GetById/${id}`);
      const item = res.data?.data || res.data || null;

      if (!item?.id) {
        return toast.error("Ma'lumot topilmadi");
      }

      setEditingContent({
        id: item.id,
        title: item.title || "",
        shortDescription: item.shortDescription || "",
        fullDescription: item.fullDescription || "",
        imageUrl: item.imageLink
          ? item.imageLink.startsWith("http")
            ? item.imageLink
            : `${API_BASE}/${item.imageLink}`
          : null,
      });

      setShowForm(true);
    } catch {
      toast.error("Ma'lumotlarni yuklab bo'lmadi");
    }
  };

  const handleUpdate = async (formData) => {
    if (!editingContent?.id) return;

    try {
      const textData = new FormData();
      textData.append("Id", editingContent.id);
      textData.append("Title", formData.title.trim());
      textData.append(
        "ShortDescription",
        formData.shortDescription?.trim() || formData.title.trim()
      );
      textData.append(
        "FullDescription",
        formData.fullDescription?.trim() || ""
      );

      await api.put(
        `/api/admin/AboutPage/Update/${editingContent.id}`,
        textData
      );

      if (formData.image && typeof formData.image !== "string") {
        const imageData = new FormData();
        imageData.append("img", formData.image);
        await api.put(
          `/api/admin/AboutPage/UpdateImage/${editingContent.id}`,
          imageData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      }

      toast.success("Ma'lumotlar yangilandi!");
      setShowForm(false);
      setEditingContent(null);
      fetchContents();
    } catch (err) {
      const errorMsg =
        err.response?.data?.errorResult?.errorMessage ||
        err.response?.data?.message ||
        "Yangilashda xato";
      toast.error(errorMsg);
    }
  };

  const handleSubmit = (formData) => {
    if (editingContent) {
      handleUpdate(formData);
    } else {
      handleAdd(formData);
    }
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

      <div className="banner-page-wrapper">
        <motion.div
          className="banner-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="breadcrumb">
            <motion.div
              whileHover={{ rotate: 360, scale: 1.15 }}
              transition={{ duration: 0.5 }}
              onClick={goBack}
            >
              <BiHomeAlt className="home-icon" />
            </motion.div>
            <span>/ Biz haqimizda</span>
          </div>

          <motion.button
            className="add-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => {
              setEditingContent(null);
              setShowForm(true);
            }}
          >
            <GoPlus /> Yangi qo‘shish
          </motion.button>
        </motion.div>

        {showForm ? (
          <ContentControllerForm
            initialData={editingContent}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingContent(null);
            }}
          />
        ) : loading ? (
          <div className="loading-container">
            <FiLoader size={48} className="spin" />
            <p>Yuklanmoqda...</p>
          </div>
        ) : contents.length === 0 ? (
          <div className="empty-state">
            <p>Hozircha ma'lumot yo‘q</p>
            <p>"Yangi qo‘shish" tugmasi orqali birinchisini yarating</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            <table className="banner-table">
              <thead>
                <tr>
                  <th>№</th>
                  <th>Sarlavha</th>
                  <th>Status</th>
                  <th>Amallar</th>
                </tr>
              </thead>
              <tbody>
                {contents.map((content, index) => (
                  <motion.tr
                    key={content.id}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <td>{index + 1}</td>
                    <td className="title-cell">{content.title}</td>
                    <td>
                      <span
                        className={`badge ${
                          content.displayStatus?.toLowerCase() || "pending"
                        }`}
                      >
                        {content.displayStatus === "Confirmed" &&
                          "Tasdiqlangan"}
                        {content.displayStatus === "Modified" &&
                          "O‘zgartirilgan"}
                        {content.displayStatus === "Cancelled" &&
                          "Bekor qilingan"}
                        {content.displayStatus === "Pending" && "Kutilmoqda"}
                        {!content.displayStatus && "Kutilmoqda"}
                      </span>
                    </td>
                    <td>
                      <AboutPageActionsDropdown
                        content={content}
                        onEdit={() => handleEdit(content.id)}
                        onView={() => handleView(content.id)}
                        refreshList={fetchContents}
                      />
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        )}
        <AboutPageViewModal
          isOpen={viewModalOpen}
          onClose={() => {
            setViewModalOpen(false);
            setSelectedContent(null);
          }}
          content={selectedContent}
          loading={viewLoading}
        />
      </div>
    </>
  );
};

export default ContentController;
